import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Alert,
  Snackbar,
  TextField,
  Autocomplete,
  Stack,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material';
import { 
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { rbacAPI, userAPI } from '../../services/api';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';
import { getResourceLabel } from '../../hooks/usePermissions';

const RBACAssignment = () => {
  const { can } = useAccessControl();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [permissionSearch, setPermissionSearch] = useState('');
  const [originalRole, setOriginalRole] = useState('');
  const [originalPermissions, setOriginalPermissions] = useState([]);

  const canReadPermissionCatalog = can(RESOURCES.PERMISSIONS, ACTIONS.READ);
  const canAssignDirectPermissions = can(RESOURCES.USERS, ACTIONS.ASSIGN);
  const canUpdateUserRole = can(RESOURCES.USERS, ACTIONS.UPDATE);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserPermissions();
      const roleId = selectedUser.role_id || '';
      setSelectedRole(roleId);
      setOriginalRole(roleId);
    } else {
      setRolePermissions([]);
      setSelectedPermissions([]);
      setSelectedRole('');
      setOriginalRole('');
      setOriginalPermissions([]);
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      const userData = response.data?.data || response.data || [];
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await rbacAPI.getRoles();
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await rbacAPI.getPermissions();
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchUserPermissions = async () => {
    setLoading(true);
    try {
      const response = await rbacAPI.getUserPermissions(selectedUser.id);
      const userPerms = response.data.userPermissions || [];
      const rolePerms = response.data.rolePermissions || [];
      
      setRolePermissions(rolePerms);
      
      // Selected permissions represent direct user grants only.
      const directPermissionIds = [...new Set(userPerms.map((up) => up.permission.id))];
      setSelectedPermissions(directPermissionIds);
      setOriginalPermissions(directPermissionIds);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      setSnackbar({ open: true, message: 'Failed to load permissions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!canUpdateUserRole) {
      setSnackbar({ open: true, message: 'You are not allowed to update user roles', severity: 'error' });
      return;
    }
    if (!selectedUser) {
      setSnackbar({ open: true, message: 'Please select a user first', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await userAPI.update(selectedUser.id, { role_id: selectedRole });
      setSnackbar({ open: true, message: 'Role assigned successfully', severity: 'success' });
      setOriginalRole(selectedRole);
      await fetchUserPermissions();
      await fetchUsers();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to assign role', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPermissions = async () => {
    if (!canAssignDirectPermissions) {
      setSnackbar({ open: true, message: 'You are not allowed to assign direct permissions', severity: 'error' });
      return;
    }
    if (!selectedUser) {
      setSnackbar({ open: true, message: 'Please select a user first', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await rbacAPI.assignPermissions({
        user_id: selectedUser.id,
        permission_ids: selectedPermissions
      });
      setSnackbar({ open: true, message: 'Permissions assigned successfully', severity: 'success' });
      setOriginalPermissions(selectedPermissions);
      await fetchUserPermissions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to assign permissions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const hasRoleChanged = () => selectedRole !== originalRole;
  
  const hasPermissionsChanged = () => {
    if (selectedPermissions.length !== originalPermissions.length) return true;
    const sorted1 = [...selectedPermissions].sort();
    const sorted2 = [...originalPermissions].sort();
    return JSON.stringify(sorted1) !== JSON.stringify(sorted2);
  };

  const filteredPermissions = canReadPermissionCatalog
    ? permissions.filter(permission => 
        permission.resource?.name?.toLowerCase().includes(permissionSearch.toLowerCase()) ||
        permission.action?.toLowerCase().includes(permissionSearch.toLowerCase()) ||
        permission.description?.toLowerCase().includes(permissionSearch.toLowerCase())
      )
    : [];

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Select User</Typography>
          <Autocomplete
            fullWidth
            value={selectedUser}
            onChange={(e, newValue) => setSelectedUser(newValue)}
            options={Array.isArray(users) ? users : []}
            getOptionLabel={(option) => option ? `${option.name} (${option.email || option.phone_number || 'No contact'})` : ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField {...params} label="Search User" placeholder="Type to search..." variant="outlined" fullWidth />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email || option.phone_number || 'No contact'} - {option.role_relation?.name || 'No Role'}
                  </Typography>
                </Box>
              </li>
            )}
          />
        </CardContent>
      </Card>

      {selectedUser && (
        <Grid container spacing={{ xs: 2, md: 2, lg: 4 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Role Assignment</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Role</InputLabel>
                    <Select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      disabled={loading || !canUpdateUserRole}
                      label="Select Role"
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{role.name}</Typography>
                            {role.description && (
                              <Typography variant="caption" color="text.secondary">
                                {role.description}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleAssignRole}
                    disabled={!selectedRole || loading || !hasRoleChanged() || !canUpdateUserRole}
                    fullWidth
                    sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                  >
                    Update Role
                  </Button>
                  {!canUpdateUserRole && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You do not have permission to change user roles.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Permissions Overview</Typography>
                    <Button
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={handleAssignPermissions}
                      disabled={loading || !hasPermissionsChanged() || !canAssignDirectPermissions}
                      size="small"
                      sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                    >
                      Save Permissions
                    </Button>
                  </Stack>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search permissions..."
                    value={permissionSearch}
                    onChange={(e) => setPermissionSearch(e.target.value)}
                    disabled={!canAssignDirectPermissions}
                    sx={{ mb: 2 }}
                  />
                  {!canAssignDirectPermissions && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You do not have permission to assign direct user permissions.
                    </Alert>
                  )}
                  
                  {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#2e7d32' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'white', width: 50 }}>Select</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'white' }}>Resource</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'white' }}>Action</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontWeight: 600, color: 'white' }}>Source</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontWeight: 600, color: 'white' }}>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredPermissions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                <Stack alignItems="center" spacing={1}>
                                  <CheckCircleIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                                  <Typography color="text.secondary" variant="body2">
                                    No permissions available
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredPermissions.map((permission) => {
                              const isFromRole = rolePermissions.some(rp => rp.permission.id === permission.id);
                              const isDirectUser = selectedPermissions.includes(permission.id);
                              const isChecked = isDirectUser || isFromRole;
                              const isRoleInheritedOnly = isFromRole && !isDirectUser;
                              return (
                                <TableRow key={permission.id} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                                  <TableCell>
                                    <Checkbox
                                      checked={isChecked}
                                      disabled={loading || !canAssignDirectPermissions || isRoleInheritedOnly}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedPermissions((prev) => (
                                            prev.includes(permission.id) ? prev : [...prev, permission.id]
                                          ));
                                        } else {
                                          setSelectedPermissions((prev) => prev.filter((id) => id !== permission.id));
                                        }
                                      }}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{getResourceLabel(permission.resource?.name) || 'N/A'}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={permission.action} size="small" sx={{ bgcolor: '#2e7d32', color: 'white' }} />
                                  </TableCell>
                                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <Stack direction="row" spacing={0.5}>
                                      {isDirectUser && <Chip label="Direct" size="small" color="primary" variant="outlined" />}
                                      {isFromRole && <Chip label="Role" size="small" color="secondary" variant="outlined" />}
                                      {!isDirectUser && !isFromRole && <Typography variant="caption" color="text.secondary">-</Typography>}
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {permission.description || 'No description'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RBACAssignment;


